import { useState } from "react";
import type { Contact, FollowUp, Job } from "@career-ops-amplify/shared";
import { useFollowups, useLogFollowup } from "@/hooks/useFollowups";
import { useLogContactInteraction } from "@/hooks/useContacts";

interface FollowUpPanelProps {
  job?: Job;
  contacts?: Contact[];
  compact?: boolean;
}

export function FollowUpPanel({ job, contacts = [], compact }: FollowUpPanelProps) {
  const { data, isLoading } = useFollowups();
  const logFollowup = useLogFollowup();
  const logInteraction = useLogContactInteraction();

  const [contactId, setContactId] = useState("");
  const [channel, setChannel] = useState("email");
  const [notes, setNotes] = useState("");
  const [nextFollowUp, setNextFollowUp] = useState("");

  const followups = data?.followups ?? [];
  const jobFollowups = job
    ? followups.filter((f) => f.jobId === job.id)
    : followups;

  async function handleLog() {
    if (!job) return;
    const contact = contacts.find((c) => c.id === contactId);

    await logFollowup.mutateAsync({
      jobId: job.id,
      company: job.company,
      role: job.role,
      companySlug: job.companySlug,
      contactId: contactId || undefined,
      contactName: contact?.name,
      channel,
      notes: notes.trim() || undefined,
      nextFollowUp: nextFollowUp || undefined,
      cadenceStatus: "logged",
    });

    if (contactId && notes.trim()) {
      await logInteraction.mutateAsync({
        id: contactId,
        notes: notes.trim(),
        channel,
      });
    }

    setNotes("");
    setNextFollowUp("");
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading follow-ups…</p>;
  }

  return (
    <div className={compact ? "space-y-3" : "rounded-lg border border-border bg-card p-4"}>
      {!compact && (
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Follow-ups
        </h3>
      )}

      {job && (
        <div className="space-y-2 rounded-md border border-border bg-muted/10 p-3">
          <div className="grid gap-2 sm:grid-cols-2">
            {contacts.length > 0 && (
              <select
                value={contactId}
                onChange={(e) => setContactId(e.target.value)}
                className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
              >
                <option value="">No linked contact</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
            >
              <option value="email">Email</option>
              <option value="linkedin">LinkedIn</option>
              <option value="phone">Phone</option>
            </select>
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="What did you send or discuss?"
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={nextFollowUp}
            onChange={(e) => setNextFollowUp(e.target.value)}
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={handleLog}
            disabled={logFollowup.isPending}
            className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
          >
            {logFollowup.isPending ? "Logging…" : "Log follow-up"}
          </button>
        </div>
      )}

      <FollowUpList items={job ? jobFollowups : followups.slice(0, 10)} />
    </div>
  );
}

function FollowUpList({ items }: { items: FollowUp[] }) {
  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No follow-ups logged yet.</p>
    );
  }

  return (
    <ul className="space-y-2">
      {items.map((f) => (
        <li
          key={f.followupId ?? `${f.appNum}-${f.nextFollowUp}`}
          className="rounded-md border border-border bg-card px-3 py-2 text-xs"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-foreground">
              {f.company} — {f.role}
            </span>
            <span className="text-muted-foreground">
              {f.nextFollowUp ? new Date(f.nextFollowUp).toLocaleDateString() : "—"}
            </span>
          </div>
          {(f.contactName || f.channel) && (
            <p className="mt-0.5 text-muted-foreground">
              {[f.contactName, f.channel].filter(Boolean).join(" · ")}
            </p>
          )}
          {f.notes && <p className="mt-1 text-foreground">{f.notes}</p>}
        </li>
      ))}
    </ul>
  );
}
